import { Link } from 'react-router-dom'

import { DataTable, type Column } from '@/components/data/DataTable'
import { StatusText } from '@/components/data/StatusText'
import { MorphLabel } from '@/components/MorphLabel'
import { Button } from '@/components/ui/button'

export type OpChange = { action: string; target: string }

export type OpResult = {
  run_id: string
  status: string
  changes: OpChange[]
  findings: Record<string, unknown>[]
  warnings: string[]
  confirm_token?: string | null
  atlassian_silent_failure: boolean
  blast_radius?: { change_count: number; projects: number; summary: string } | null
  required_fields?: Record<
    string,
    { name: string | null; required: boolean; allowed_values: string[] | null }
  > | null
}

/** A blast radius from this many changes up is called out as high impact. */
const HIGH_IMPACT = 100

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** Change count per action, in first-seen order: [['add', 34], ['remove', 3]]. */
function actionCounts(changes: OpChange[]): [string, number][] {
  const m = new Map<string, number>()
  for (const c of changes) m.set(c.action, (m.get(c.action) ?? 0) + 1)
  return [...m]
}

const REMOVING = /^(remove|delete|unassign|revoke|disable|archive|trash)/i
const ADDING = /^(add|create|assign|invite|grant|enable|set|copy)/i

function ActionCell({ action }: { action: string }) {
  if (REMOVING.test(action)) return <span className="text-danger">− {action}</span>
  if (ADDING.test(action)) return <span className="text-ok">+ {action}</span>
  return <span className="text-text-2">{action}</span>
}

const H = 'text-[11px] font-medium uppercase tracking-[0.07em] text-text-3'

type Row = OpChange & { i: number }

const CHANGE_COLUMNS: Column<Row>[] = [
  { key: 'action', header: 'Action', width: '34%', render: (r) => <ActionCell action={r.action} /> },
  { key: 'target', header: 'Target', mono: true },
]

/**
 * Right column of a mutating operation: an empty state, then the preview (counts, impact,
 * changes, warnings) with the confirm button under it, then the result in the same place.
 */
export function PreviewPane({ preview, result, audited, critical, executing, progress, onConfirm }: {
  preview: OpResult | null
  result: OpResult | null
  /** the result is an applied mutation recorded in the audit log — only then link to it */
  audited: boolean
  critical: boolean
  executing: boolean
  progress: number
  onConfirm: () => void
}) {
  if (result) return <ResultView result={result} audited={audited} />
  if (!preview) {
    return (
      <p className="px-4 py-3.5 text-[12.5px] text-text-3">Preview shows exactly what will change.</p>
    )
  }

  const n = preview.changes.length
  const big = (preview.blast_radius?.change_count ?? n) >= HIGH_IMPACT
  const label = critical
    ? `Execute${preview.confirm_token ? ' (confirm)' : ''}`
    : `${preview.confirm_token ? 'Confirm' : 'Apply'}${n === 0 ? '' : ` ${plural(n, 'change')}`}`
  const required = preview.required_fields ? Object.entries(preview.required_fields) : []

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 space-y-3 px-4 pb-3 pt-3.5">
        <h2 className={`${H} flex items-center gap-2`}>
          Preview
          {preview.confirm_token && <span className="normal-case tracking-normal text-text-2">· confirm required</span>}
        </h2>
        <div aria-label="Preview summary" role="group" className="flex flex-wrap gap-x-4 gap-y-1">
          {actionCounts(preview.changes).map(([action, count]) => (
            <span key={action} data-testid="summary-count">
              <span className="font-mono text-[18px] tabular-nums text-foreground">{count}</span>{' '}
              <span className="text-text-3">{action}</span>
            </span>
          ))}
          {n === 0 && (
            <span data-testid="summary-count">
              <span className="font-mono text-[18px] tabular-nums text-foreground">0</span>{' '}
              <span className="text-text-3">changes</span>
            </span>
          )}
          <span data-testid="summary-count">
            <span className="font-mono text-[18px] tabular-nums text-foreground">{preview.warnings.length}</span>{' '}
            <span className="text-text-3">{preview.warnings.length === 1 ? 'warning' : 'warnings'}</span>
          </span>
        </div>
        {preview.blast_radius?.summary && (
          <p className="text-[12px] text-text-2">
            {big && <span className="font-medium text-danger">high impact · </span>}
            Impact: <span className="font-mono">{preview.blast_radius.summary}</span>
          </p>
        )}
        {required.length > 0 && (
          <section>
            <h3 className={`${H} mb-1`}>Screen fields</h3>
            <ul className="space-y-0.5 text-[12px] text-text-2">
              {required.map(([fid, m]) => (
                <li key={fid} className="min-w-0 truncate" title={m.allowed_values?.join(', ')}>
                  <span className="text-foreground">{m.name ?? fid}</span>
                  {m.required && <span className="text-danger"> required</span>}
                  {m.allowed_values && m.allowed_values.length > 0 && (
                    <span className="text-text-3"> — {m.allowed_values.join(', ')}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <div className="flex min-h-0 flex-1 flex-col border-t border-line">
        <DataTable
          label="Changes"
          rows={preview.changes.map((c, i) => ({ ...c, i }))}
          columns={CHANGE_COLUMNS}
          rowId={(r) => String(r.i)}
          empty="Nothing to change — already in the desired state."
        />
      </div>
      <div className="shrink-0 space-y-2.5 border-t border-line px-4 py-3">
        {preview.warnings.length > 0 && (
          <ul aria-label="Warnings" className="max-h-28 space-y-1 overflow-y-auto text-[12px] text-text-2">
            {preview.warnings.map((w, i) => (
              <li key={i}><span className="font-medium text-warn">warning</span> {w}</li>
            ))}
          </ul>
        )}
        <Button
          variant={critical ? 'destructive' : 'default'}
          onClick={onConfirm}
          disabled={executing}
        >
          <MorphLabel>{executing ? (critical ? 'Executing…' : 'Applying…') : label}</MorphLabel>
        </Button>
        {executing && (
          <div className="progress-track" role="progressbar" aria-label="Executing" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
    </div>
  )
}

function ResultView({ result, audited }: { result: OpResult; audited: boolean }) {
  const n = result.changes.length
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 space-y-2 px-4 pb-3 pt-3.5">
        <h2 className={H}>Result</h2>
        <p className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
          <StatusText status={result.status} />
          <span className="text-text-2">
            <span className="font-mono tabular-nums text-foreground">{n}</span> {n === 1 ? 'change' : 'changes'} applied
          </span>
        </p>
        {audited && (
          <Link
            to={`/audit/${result.run_id}`}
            className="inline-block text-[12px] text-text-2 underline decoration-line-strong underline-offset-4 transition-colors hover:text-foreground"
          >
            View in audit log <span className="font-mono text-text-3">{result.run_id.slice(0, 8)}</span>
          </Link>
        )}
        {result.atlassian_silent_failure && (
          <p className="text-[12px] text-text-2">
            <span className="font-medium text-danger">not applied</span> Atlassian acknowledged but did not apply the change — verify manually.
          </p>
        )}
        {result.warnings.length > 0 && (
          <ul aria-label="Warnings" className="space-y-1 text-[12px] text-text-2">
            {result.warnings.map((w, i) => (
              <li key={i}><span className="font-medium text-warn">warning</span> {w}</li>
            ))}
          </ul>
        )}
      </div>
      {n > 0 && (
        <div className="flex min-h-0 flex-1 flex-col border-t border-line">
          <DataTable
            label="Applied changes"
            rows={result.changes.map((c, i) => ({ ...c, i }))}
            columns={CHANGE_COLUMNS}
            rowId={(r) => String(r.i)}
          />
        </div>
      )}
    </div>
  )
}
